import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { Search } from 'lucide-react';

const KanbanColumn = ({
  id,
  title,
  icon: Icon,
  count,
  items,
  searchQuery,
  onSearchChange,
  renderCard,
  droppable = false,
  accepts = [],
  footerAction,
  emptyMessage = 'Aucun élément'
}) => {
  const { isOver, setNodeRef } = useDroppable({
    id: `column-${id}`,
    data: {
      type: 'column',
      columnId: id,
      accepts
    },
    disabled: !droppable
  });

  const columnStyle = {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f3efe7',
    borderRadius: '0.75rem',
    border: '1px solid #e5e0d5',
    minWidth: '250px',
    flex: '1 1 0',
    /*
     * La colonne suit la hauteur de sa zone, elle ne l'estime plus.
     *
     * `calc(100vh - 200px)` retranchait à la fenêtre une hauteur d'en-tête supposée : la colonne
     * pouvait dépasser sa zone d'affichage et son bas devenait inatteignable, la page ne défilant
     * pas sur cet écran. `100%` la borne à ce que le conteneur lui accorde réellement.
     */
    maxHeight: '100%',
    minHeight: 0,
    transition: 'all 0.2s ease'
  };

  const headerStyle = {
    padding: '1rem',
    borderBottom: '1px solid #e5e0d5',
    backgroundColor: 'white',
    borderRadius: '0.75rem 0.75rem 0 0'
  };

  const titleRowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '0.75rem'
  };

  const titleStyle = {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#1e293b',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  };

  const countBadgeStyle = {
    backgroundColor: '#e5e0d5',
    color: '#475569',
    fontSize: '0.75rem',
    fontWeight: '600',
    padding: '0.125rem 0.5rem',
    borderRadius: '9999px'
  };

  const searchContainerStyle = {
    position: 'relative'
  };

  const searchIconStyle = {
    position: 'absolute',
    left: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#94a3b8',
    pointerEvents: 'none'
  };

  const searchInputStyle = {
    width: '100%',
    padding: '0.5rem 0.75rem 0.5rem 2.25rem',
    border: '1px solid #e5e0d5',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    boxSizing: 'border-box'
  };

  const contentStyle = {
    flex: 1,
    overflowY: 'auto',
    padding: '0.75rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    backgroundColor: isOver && droppable ? '#e8e4dc' : 'transparent',
    transition: 'background-color 0.2s ease',
    minHeight: '120px'
  };

  const dropZoneStyle = {
    border: isOver && droppable ? '2px dashed #6366f1' : '2px dashed transparent',
    borderRadius: '0.5rem',
    padding: '0.5rem',
    transition: 'all 0.2s ease',
    flex: 1,
    display: 'flex',
    flexDirection: 'column'
  };

  const emptyStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    color: '#94a3b8',
    fontSize: '0.875rem',
    fontStyle: 'italic'
  };

  const actionStyle = {
    marginTop: '0.75rem'
  };

  return (
    <div style={columnStyle}>
      {/* Header avec titre, recherche et action */}
      <div style={headerStyle}>
        <div style={titleRowStyle}>
          {Icon && <Icon size={18} style={{ color: '#64748b' }} />}
          <span style={titleStyle}>{title}</span>
          <span style={countBadgeStyle}>{count}</span>
        </div>
        <div style={searchContainerStyle}>
          <Search size={16} style={searchIconStyle} />
          <input
            type="text"
            placeholder="Rechercher..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            style={searchInputStyle}
            onFocus={(e) => {
              e.target.style.borderColor = '#1a1a1a';
              e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e5e0d5';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>
        {footerAction && (
          <div style={actionStyle}>
            {footerAction}
          </div>
        )}
      </div>

      {/* Contenu scrollable */}
      <div
        ref={droppable ? setNodeRef : undefined}
        style={contentStyle}
      >
        <div style={dropZoneStyle}>
          {items.length === 0 ? (
            <div style={emptyStyle}>{emptyMessage}</div>
          ) : (
            items.map((item, index) => (
              <React.Fragment key={item.id || index}>
                {renderCard(item)}
              </React.Fragment>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default KanbanColumn;
